import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import { PaymentMethodCode } from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'

import AffirmModal from '../components/AffirmModal'
import KlarnaPaymentSummaryModal from '../components/KlarnaPaymentSummaryModal'
import KlarnaThirdPartyAdvertisement from '../components/KlarnaThirdPartyAdvertisement/KlarnaThirdPartyAdvertisement.vue'
import OneyModal from '../components/OneyModal'
import PaypalThirdPartyAdvertisement from '../components/PaypalThirdPartyAdvertisement/PaypalThirdPartyAdvertisement.vue'
import { isPaypalModalAvailable } from '../components/PaypalThirdPartyAdvertisement/isPaypalModalAvailable'
import type { PaymentAdvertisingConfig } from '../types/PaymentAdvertisingConfig'
import type { PaymentAdvertisingId } from '../types/PaymentAdvertisingId'
import { availableWhenAllOf } from '../utils/availableWhenAllOf'
import { availableWhenAnyOf } from '../utils/availableWhenAnyOf'
import { availableWith } from '../utils/availableWith'

import translations from './advertising.translations'

/**
 * Main advertising registry.
 *
 * Each key is a stable {@link PaymentAdvertisingId} and each value describes:
 * - how to decide whether this setup can be advertised (`isAvailableFor`), and
 * - which UI configuration to render per {@link PaymentAdvertisingSpot spot}.
 *
 * This registry is consumed by selection utilities (ex: `findAvailablePaymentAdvertisement`)
 * and by the main renderer component (`PaymentAdvertisement`).
 */
export const PAYMENT_METHODS_ADVERTISING: Record<
  PaymentAdvertisingId,
  PaymentAdvertisingConfig
> = {
  oneyGeneric: {
    isAvailableFor: availableWhenAnyOf(
      PaymentMethodCode.ONEY3X,
      PaymentMethodCode.ONEY4X,
      PaymentMethodCode.ONEY6X,
      PaymentMethodCode.ONEY10X,
      PaymentMethodCode.ONEY12X,
    ),
    cart: {
      isHandledByThirdParty: false,
      available: translations.installmentPaymentAvailable,
      unavailable: translations.installmentPaymentUnavailable,
      modal: OneyModal,
    },
    orders: {
      isHandledByThirdParty: false,
      text: translations.paidWithOney,
      modal: null,
    },
    product: {
      isHandledByThirdParty: false,
      text: translations.payInInstallments,
      modal: OneyModal,
    },
  },
  affirm: {
    isAvailableFor: availableWith(PaymentMethodCode.AFFIRM),
    cart: {
      isHandledByThirdParty: false,
      available: translations.installmentPaymentAvailable,
      unavailable: translations.installmentPaymentUnavailable,
      modal: AffirmModal,
    },
    product: {
      isHandledByThirdParty: false,
      text: translations.payInInstallments,
      modal: AffirmModal,
    },
  },

  /**
   * Advertising configuration shared by all Klarna methods when all of them are available.
   * (except pay now. as it's not really a per installment payment)
   */
  klarnaAll: {
    isAvailableFor: availableWhenAllOf(
      PaymentMethodCode.KLARNA_PAY_LATER,
      PaymentMethodCode.KLARNA_SLICE_IT,
    ),
    cart: {
      isHandledByThirdParty: true,
      unavailable: translations.installmentPaymentUnavailable,
      component: KlarnaThirdPartyAdvertisement,
    },
  },
  /**
   * Advertising configuration shared by all Klarna methods when any of them is available.
   */
  klarnaOneOf: {
    isAvailableFor: availableWhenAnyOf(
      PaymentMethodCode.KLARNA_PAY_LATER,
      PaymentMethodCode.KLARNA_SLICE_IT,
      PaymentMethodCode.KLARNA_PAY_NOW,
    ),
    product: {
      isHandledByThirdParty: true,
      component: KlarnaThirdPartyAdvertisement,
    },
  },

  klarnaPayLater: {
    isAvailableFor: availableWith(PaymentMethodCode.KLARNA_PAY_LATER),
    orders: {
      isHandledByThirdParty: false,
      text: translations.paidWithKlarnaPayLater,
      modal: KlarnaPaymentSummaryModal,
    },
    cart: {
      isHandledByThirdParty: true,
      unavailable: translations.payLaterUnavailable,
      component: KlarnaThirdPartyAdvertisement,
    },
  },
  klarnaSliceIt: {
    isAvailableFor: availableWith(PaymentMethodCode.KLARNA_SLICE_IT),
    cart: {
      isHandledByThirdParty: true,
      unavailable: translations.installmentPaymentUnavailable,
      component: KlarnaThirdPartyAdvertisement,
    },
    orders: {
      isHandledByThirdParty: false,
      text: translations.paidWithKlarnaSliceIt,
      modal: KlarnaPaymentSummaryModal,
    },
  },
  paypal: {
    isAvailableFor: isPaypalModalAvailable,
    cart: {
      isHandledByThirdParty: true,
      unavailable: translations.paypalPaymentUnavailable,
      component: PaypalThirdPartyAdvertisement,
    },
    product: {
      isHandledByThirdParty: true,
      component: PaypalThirdPartyAdvertisement,
    },
  },
}

/**
 * Default priority order, derived from `PAYMENT_METHODS_ADVERTISING` insertion order.
 *
 * This is used as a baseline when building per-country priority overrides.
 */
export const AdvertisingDefaultOrder = Object.keys(
  PAYMENT_METHODS_ADVERTISING,
) as PaymentAdvertisingId[]

const AdvertisingPaypalFirst: PaymentAdvertisingId[] = [
  'paypal',
  ...AdvertisingDefaultOrder,
]

/**
 * Optional per-country priority overrides.
 *
 * When a country is present, providers are evaluated in the specified order.
 * Countries not listed here fall back to {@link AdvertisingDefaultOrder}.
 */
export const PAYMENT_METHODS_ADVERTISING_BY_COUNTRY: Partial<
  Record<MarketCountryCode, PaymentAdvertisingId[]>
> = {
  ES: AdvertisingPaypalFirst,
  FR: AdvertisingPaypalFirst,
  IT: AdvertisingPaypalFirst,
}
